"use client";

import FAQ from "@/components/FAQ";
import Footer from "@/components/Footer";
import { UNUSUAL_TEXT_KEY } from "@/components/unusual/constants";
import br from "@/styles/breakpoints.module.css";
import { spacing } from "@/utils/spacing";
import { themeColors } from "@/utils/themeColors";
import { FlashcardSet, Note } from "@knowt/syncing/graphql/schema";
import clsx from "clsx";
import { ReviewType } from "../LandingPages/LandingMainPage/constants";
import ExploreSection from "../LandingPages/UploadLandingPages/components/ExploreSection";
import UsersFeedbacks from "../LandingPages/UploadLandingPages/components/UsersFeedbacks";
import { QuizletImportLinkInput } from "../LandingPages/UploadLandingPages/components/AIStudyToolsSection";
import styles from "../LandingPages/UploadLandingPages/components/medias/video-summarizer.module.css";
import CompliantsSection from "../LandingPages/components/CompliantsSection";
import LandingPagesSwitcher from "../LandingPages/components/LandingPagesSwitcher";
import SectionContainer from "../LandingPages/components/SectionContainer";
import {
    LandingFullWidthSection,
    LandingHalfSection,
    LandingPageWrapper,
} from "../LandingPages/components/styled/Containers";
import AIToolPicker from "./components/AIToolPicker";
import { DefaultBenefits, TEACHER_AI_TOOLS_FAQS } from "./utils/constants";
import CompetitorCards from "../BulkPricingPage/components/CompetitorCards";
import CompetitorsComparisonTable from "../LandingPages/CompetitorPages/components/CompetitorsComparisionTable";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import { FlexColumnAlignJustifyCenter, FlexRowAlignCenter } from "@/components/Flex";
import BenefitsCards from "./components/BenefitsCards";
import BrowseEncouragementCards from "../LandingPages/components/BrowseEncouragmentCards";
import { ASSETS_URL } from "@/config/deployConstants";

const TeacherAITools = ({
    topNotes,
    topFlashcardSets,
}: {
    topNotes: Note[];
    topFlashcardSets: FlashcardSet[];
}) => {
    const { user } = useCurrentUser();

    return (
        <LandingPageWrapper>
            <LandingFullWidthSection
                style={{
                    marginBlock: `${spacing.LG_2} ${spacing.LG_3}`,
                    marginInline: "4rem 4rem",
                    gap: spacing.LG_2,
                }}>
                {!user && (
                    <LandingPagesSwitcher
                        pages={[
                            { name: "For admins", route: "/schools/admin" },
                            { name: "For teachers", route: "/free-ai-tools-for-teachers" },
                        ]}
                        containerStyle={{ backgroundColor: themeColors.background, padding: 0 }}
                    />
                )}

                <SectionContainer
                    isPageHeader
                    title="Free AI Tools for Teachers"
                    subtitle={
                        user
                            ? "Make teaching 50% faster by using our thoughtfully crafted teacher tools."
                            : "Paste your Quizlet link below to bring it over in seconds! Or, make teaching 50% faster by using our thoughtfully crafted teacher tools."
                    }
                    unusualKey={UNUSUAL_TEXT_KEY.AI_TEACHER_TOOLS_MAIN}
                    style={{ width: "unset" }}
                    titleStyle={{ fontSize: "5rem", fontWeight: "800" }}>
                    {!user && <QuizletImportLinkInput />}
                </SectionContainer>

                <AIToolPicker />
            </LandingFullWidthSection>

            {!user && (
                <>
                    <LandingHalfSection style={{ padding: "8rem 0", gap: spacing.LG_2 }}>
                        <FlexColumnAlignJustifyCenter>
                            <h2 className="heading2" style={{ marginBottom: spacing.SM }}>
                                Get 1 full school year for $0
                            </h2>
                            <p className="bodyXl" style={{ marginBottom: spacing.LG_2 }}>
                                Tell your administrator about our exclusive offer where the entire school or district
                                can get licenses for $0 when you switch from an existing contract.
                            </p>
                        </FlexColumnAlignJustifyCenter>
                        <CompetitorCards />
                        <CompetitorsComparisonTable primaryCompetitor="MagicSchool" />
                    </LandingHalfSection>

                    <LandingHalfSection style={{ backgroundColor: themeColors.background }}>
                        <h2 className="heading2">AI for Teachers that are ahead of the curve</h2>
                        <p className="bodyXl" style={{ marginBottom: "4rem" }}>
                            Designed with you in mind, our AI tools adapt to the needs of your classroom and your
                            students.
                        </p>
                        <FlexColumnAlignJustifyCenter style={{ gap: spacing.LG_3 }}>
                            <FlexRowAlignCenter style={{ gap: spacing.MD, alignItems: "stretch", flexWrap: "wrap" }}>
                                <BenefitsCards benefits={DefaultBenefits} />
                            </FlexRowAlignCenter>
                            <FlexRowAlignCenter style={{ gap: spacing.MD, alignItems: "stretch", width: "100%" }}>
                                <BrowseEncouragementCards
                                    encouragementCards={[
                                        {
                                            title: "Become a Knowt Knight & earn!",
                                            href: "/teachers/knowt-knights-teacher-ambassadors",
                                            image: `${ASSETS_URL}/images/knowt-ambassador-benefits.svg`,
                                        },
                                        {
                                            title: "No steep learning curve",
                                            href: "https://help.knowt.com",
                                            image: `${ASSETS_URL}/images/learning-curve.svg`,
                                        },
                                    ]}
                                />
                            </FlexRowAlignCenter>
                        </FlexColumnAlignJustifyCenter>
                    </LandingHalfSection>

                    <LandingFullWidthSection
                        className={clsx([styles.feedbackContainer, br.mdDownDisplayNone])}
                        style={{
                            backgroundColor: themeColors.background,
                            maxWidth: "unset",
                            overflow: "hidden",
                            paddingBottom: 0,
                            padding: "8rem 0",
                        }}>
                        <UsersFeedbacks
                            type={ReviewType.SCHOOLS}
                            title="Trusted by 1,000+ schools"
                            isForSchools
                            subtitle={"Join our thriving community of teachers & students that trust Knowt."}
                        />
                    </LandingFullWidthSection>

                    <LandingHalfSection>
                        <CompliantsSection />
                    </LandingHalfSection>

                    <LandingHalfSection style={{ padding: "8rem 0" }}>
                        <ExploreSection topNotes={topNotes} topFlashcardSets={topFlashcardSets} examsLength={0} />
                    </LandingHalfSection>

                    <LandingHalfSection style={{ padding: "8rem 0", maxWidth: "70rem" }}>
                        <FAQ
                            faqs={TEACHER_AI_TOOLS_FAQS}
                            title={"FAQ"}
                            description={"We thought you might have some questions..."}
                            titleClassName="heading2"
                            containerStyle={{
                                marginTop: 0,
                                backgroundColor: themeColors.background,
                            }}
                        />
                    </LandingHalfSection>
                </>
            )}
            <Footer landing />
        </LandingPageWrapper>
    );
};

export default TeacherAITools;
